import { DateTime } from "@bps/date-time";
import { CalendarEventType } from "@libs/gateways/booking/BookingGateway.dtos.ts";
import { OrgUnit } from "@stores/core/models/OrgUnit.ts";
import { PracOrgUnit } from "@stores/practice/models/PracOrgUnit.ts";
import { RootStore } from "@stores/root/RootStore.ts";

export interface OrgUnitScreenData {
  pracOrgUnit: PracOrgUnit;
  orgUnit: OrgUnit;
  hasAppointments: boolean;
  hasMeetings: boolean;
  hasUnavailables: boolean;
  hasFutureWaitingListEntries: boolean;
}

export const fetchOrgUnitScreenData = async (
  root: RootStore,
  locationId: string
): Promise<OrgUnitScreenData> => {
  const [pracOrgUnit, orgUnit] = await Promise.all([
    root.practice.getOrgUnit(locationId),
    root.core.getOrgUnit(locationId)
  ]);

  if (!pracOrgUnit) {
    throw new Error("Could not locate Prac Org Unit");
  }

  let hasAppointments = false;
  let hasMeetings = false;
  let hasUnavailables = false;

  if (pracOrgUnit.isDefault) {
    return {
      pracOrgUnit,
      orgUnit,
      hasAppointments,
      hasMeetings,
      hasUnavailables,
      hasFutureWaitingListEntries: false
    };
  }

  // No need to check for appointments or waiting list entries if location is default

  const futureAppointmentsPromise = root.booking.getFutureCalendarEvents({
    orgUnitIds: [locationId]
  });

  const hasWaitingListEntriesPromise = root.booking
    .getLocationWaitingListItems(locationId)
    .then(result =>
      result.some(
        x => DateTime.fromISO(x.until) > DateTime.today().minus({ days: 1 })
      )
    );

  const [futureAppointments, hasFutureWaitingListEntries] = await Promise.all([
    futureAppointmentsPromise,
    hasWaitingListEntriesPromise
  ]);

  futureAppointments.forEach(calendarEvent => {
    if (calendarEvent.type === CalendarEventType.Meeting) {
      hasMeetings = true;
    } else if (calendarEvent.type === CalendarEventType.Unavailable) {
      hasUnavailables = true;
    } else {
      hasAppointments = true;
    }
  });

  return {
    pracOrgUnit,
    orgUnit,
    hasAppointments,
    hasMeetings,
    hasUnavailables,
    hasFutureWaitingListEntries
  };
};
